/**
 * VideoModule — Kling image-to-video & start+end frame queue.
 *
 * Lives inside Production as a dedicated tab. Completely isolated from the
 * image generation flow (shares nothing except the auth context and the
 * existing references bucket).
 *
 * UX matches Kling real:
 *   - No manual aspect ratio selector. The backend inspects the start frame
 *     and picks 16:9 / 9:16 / 1:1 automatically (shown as "auto-detected").
 *   - Model tiers: Standard (económico) · Pro (premium).
 *   - Image-to-Video = start frame only + motion prompt.
 *   - Start+End Frame = toggle "Usar End Frame" reveals the second dropzone.
 *   - Duration: 5s (default) or 10s.
 *   - Uses the same async job queue pattern as Image jobs: fire-and-forget,
 *     poll every 3s, results promote into the grid when completed.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useApp } from '@/context/AppContext';
import PromptVoiceField from '@/components/shared/PromptVoiceField';
import PromptAssistant from '@/components/shared/PromptAssistant';
import {
  Loader2, Upload, Download, X, Film,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ---- XHR helpers (fetch is monkey-patched in this environment) ------------
function xhrJson(method, url, body, timeout = 30000, extraHeaders = null) {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('Accept', 'application/json');
    if (extraHeaders) Object.entries(extraHeaders).forEach(([k, v]) => v && xhr.setRequestHeader(k, v));
    xhr.timeout = timeout;
    xhr.onload = () => {
      let data = {};
      try { data = xhr.responseText ? JSON.parse(xhr.responseText) : {}; } catch { /* noop */ }
      resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
    };
    xhr.onerror = () => resolve({ ok: false, status: 0, data: { detail: 'Network error' } });
    xhr.ontimeout = () => resolve({ ok: false, status: 0, data: { detail: 'Timeout' } });
    xhr.send(body ? JSON.stringify(body) : null);
  });
}

function xhrUpload(url, formData, extraHeaders = null) {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    if (extraHeaders) Object.entries(extraHeaders).forEach(([k, v]) => v && xhr.setRequestHeader(k, v));
    xhr.timeout = 180000;
    xhr.onload = () => {
      let data = {};
      try { data = xhr.responseText ? JSON.parse(xhr.responseText) : {}; } catch { /* noop */ }
      resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
    };
    xhr.onerror = () => resolve({ ok: false, status: 0, data: { detail: 'Network error' } });
    xhr.ontimeout = () => resolve({ ok: false, status: 0, data: { detail: 'Timeout' } });
    xhr.send(formData);
  });
}

// ---- Frame slot -----------------------------------------------------------
function FrameSlot({ label, frame, onPickFile, onClear, testId, optional = false }) {
  const inputRef = useRef(null);
  const onChange = (e) => {
    const f = e.target.files?.[0];
    if (f) onPickFile(f);
    e.target.value = '';
  };
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
        {label}
        {optional && <span className="text-[10px] text-gray-300 normal-case">opcional</span>}
      </label>
      <div
        data-testid={testId}
        onClick={() => !frame && inputRef.current?.click()}
        className={`relative aspect-square rounded-xl border-2 border-dashed flex items-center justify-center overflow-hidden transition-all ${
          frame
            ? 'border-gray-200/40 bg-white/40'
            : 'border-gray-300/50 bg-white/30 hover:border-gray-400/70 cursor-pointer'
        }`}
      >
        <input ref={inputRef} type="file" accept="image/*" onChange={onChange} className="hidden" />
        {frame ? (
          <>
            <img
              src={frame.url}
              alt={frame.name}
              className="w-full h-full object-cover"
            />
            <button
              data-testid={`${testId}-clear`}
              onClick={(e) => { e.stopPropagation(); onClear(); }}
              className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center"
              title="Quitar"
            >
              <X className="w-3.5 h-3.5" />
            </button>
            <div className="absolute left-2 bottom-2 px-2 py-0.5 rounded-md bg-black/55 text-white text-[10px] max-w-[calc(100%-1rem)] truncate">
              {frame.name}
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center gap-2 text-gray-400">
            <Upload className="w-5 h-5" />
            <span className="text-[11px]">Click para subir</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ---- Main component -------------------------------------------------------
export default function VideoModule({ projectId }) {
  const { session } = useAuth();
  const accessToken = session?.access_token || null;
  const { productionRef, setProductionRef, projects } = useApp();
  const authHeader = accessToken ? { Authorization: `Bearer ${accessToken}` } : null;

  // Form state
  const [model, setModel] = useState('kling-2.6-pro'); // 'kling-2.6-standard' | 'kling-2.6-pro'
  const [duration, setDuration] = useState(5);
  const [prompt, setPrompt] = useState('');
  const [useEndFrame, setUseEndFrame] = useState(false);
  const [startFrame, setStartFrame] = useState(null); // {url, name, storage_path}
  const [endFrame, setEndFrame] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingSlot, setUploadingSlot] = useState(null); // 'start' | 'end' | null
  const [error, setError] = useState('');

  // Jobs
  const [jobs, setJobs] = useState([]);

  // Pick up productionRef if a manager clicked "Enviar a Video" from an image
  useEffect(() => {
    if (!productionRef || productionRef.target !== 'video') return;
    if (!productionRef.url || !productionRef.storage_path) return;
    setStartFrame({
      url: productionRef.url,
      name: productionRef.name || 'start.png',
      storage_path: productionRef.storage_path,
    });
    setProductionRef(null); // consume once
  }, [productionRef, setProductionRef]);

  // Upload a file directly to the unlinked/linked storage and get back a path
  const uploadFrame = useCallback(async (file) => {
    if (!accessToken) {
      setError('Sesión no encontrada. Inicia sesión para subir frames.');
      return null;
    }
    const fd = new FormData();
    fd.append('file', file);
    const url = projectId
      ? `${API}/api/storage/upload/${projectId}`
      : `${API}/api/storage/upload-unlinked`;
    if (projectId) {
      fd.append('bucket', 'project-files');
      fd.append('source', 'team');
    }
    const { ok, data } = await xhrUpload(url, fd, authHeader);
    if (!ok) {
      setError(data?.detail || 'No se pudo subir el frame');
      return null;
    }
    return {
      name: file.name,
      url: data?.url || URL.createObjectURL(file),
      storage_path: data?.storage_path,
    };
  }, [accessToken, projectId, authHeader]);

  const handlePickStart = async (file) => {
    setUploadingSlot('start');
    setError('');
    const f = await uploadFrame(file);
    if (f) setStartFrame(f);
    setUploadingSlot(null);
  };
  const handlePickEnd = async (file) => {
    setUploadingSlot('end');
    setError('');
    const f = await uploadFrame(file);
    if (f) setEndFrame(f);
    setUploadingSlot(null);
  };

  const canSubmit = prompt.trim().length > 0 && !!startFrame?.storage_path && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setError('');
    setSubmitting(true);
    const payload = {
      prompt: prompt.trim(),
      model,
      project_id: projectId || null,
      duration_seconds: duration,
      start_frame_path: startFrame.storage_path,
      end_frame_path: useEndFrame ? (endFrame?.storage_path || null) : null,
    };
    const { ok, data } = await xhrJson(
      'POST', `${API}/api/generate/video/jobs`, payload, 30000, authHeader,
    );
    setSubmitting(false);
    if (!ok) {
      setError(data?.detail || 'No se pudo crear el job de video');
      return;
    }
    if (data?.job) setJobs(prev => [data.job, ...prev.filter(j => j.id !== data.job.id)]);
    // Clear prompt so user can chain another job. Keep frames in case they
    // want to iterate with a different motion description.
    setPrompt('');
  };

  // Poll jobs list
  useEffect(() => {
    if (!accessToken) { setJobs([]); return undefined; }
    let cancelled = false;
    const tick = async () => {
      const { ok, data } = await xhrJson(
        'GET',
        `${API}/api/generate/video/jobs?limit=20&_=${Date.now()}`,
        null,
        20000,
        authHeader,
      );
      if (cancelled || !ok) return;
      setJobs(Array.isArray(data.jobs) ? data.jobs : []);
    };
    tick();
    const interval = setInterval(tick, 3000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [accessToken, authHeader]);

  const activeJobs = jobs.filter(j => j.status === 'queued' || j.status === 'processing');
  const completedJobs = jobs.filter(j => j.status === 'completed');
  const failedJobs = jobs.filter(j => j.status === 'failed');

  return (
    <div data-testid="video-module" className="p-4 md:p-6 max-w-4xl space-y-5">
      {/* Model + duration row */}
      <div className="flex flex-wrap gap-4">
        <div className="flex-1 min-w-[220px]">
          <label className="text-xs text-gray-400 uppercase tracking-wider mb-1.5 block">Modelo</label>
          <div className="flex items-center gap-1 p-1 rounded-xl bg-white/50 border border-gray-200/50">
            {[
              { v: 'kling-2.6-standard', label: 'Kling 2.6 Standard', hint: 'económico' },
              { v: 'kling-2.6-pro',      label: 'Kling 2.6 Pro',      hint: 'premium' },
            ].map(opt => (
              <button
                key={opt.v}
                data-testid={`video-model-${opt.v}`}
                onClick={() => setModel(opt.v)}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
                  model === opt.v
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-600 hover:bg-white/70'
                }`}
              >
                <span className="font-medium">{opt.label}</span>
                <span className={`text-[10px] uppercase tracking-wider ${model === opt.v ? 'text-white/70' : 'text-gray-400'}`}>
                  {opt.hint}
                </span>
              </button>
            ))}
          </div>
        </div>
        <div className="min-w-[160px]">
          <label className="text-xs text-gray-400 uppercase tracking-wider mb-1.5 block">Duración</label>
          <div className="flex items-center gap-1 p-1 rounded-xl bg-white/50 border border-gray-200/50">
            {[5, 10].map(d => (
              <button
                key={d}
                data-testid={`video-duration-${d}`}
                onClick={() => setDuration(d)}
                className={`flex-1 px-3 py-2 rounded-lg text-sm transition-all ${
                  duration === d ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-white/70'
                }`}
              >
                {d}s
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Frames */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs text-gray-400 uppercase tracking-wider">Frames</label>
          <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer select-none" data-testid="video-end-frame-toggle">
            <input
              type="checkbox"
              checked={useEndFrame}
              onChange={(e) => {
                setUseEndFrame(e.target.checked);
                if (!e.target.checked) setEndFrame(null);
              }}
              className="accent-emerald-500"
            />
            Usar End Frame (transición)
          </label>
        </div>
        <div className={`grid gap-3 ${useEndFrame ? 'grid-cols-2' : 'grid-cols-1 sm:grid-cols-2'}`}>
          <FrameSlot
            label={useEndFrame ? 'Start Frame' : 'Imagen base'}
            frame={startFrame}
            testId="video-start-frame"
            onPickFile={handlePickStart}
            onClear={() => setStartFrame(null)}
          />
          {useEndFrame && (
            <FrameSlot
              label="End Frame"
              frame={endFrame}
              testId="video-end-frame"
              optional
              onPickFile={handlePickEnd}
              onClear={() => setEndFrame(null)}
            />
          )}
        </div>
        {uploadingSlot && (
          <div className="mt-1.5 text-[11px] text-gray-400 flex items-center gap-1.5">
            <Loader2 className="w-3 h-3 animate-spin" />
            Subiendo {uploadingSlot === 'start' ? 'start' : 'end'} frame…
          </div>
        )}
        <p className="mt-1.5 text-[11px] text-gray-400">
          El formato del video se detecta automáticamente desde la imagen base: vertical, horizontal o cuadrado.
        </p>
      </div>

      {/* Motion prompt */}
      <div className="space-y-2">
        <label className="text-xs text-gray-400 uppercase tracking-wider mb-1.5 block">Prompt de movimiento</label>
        <PromptAssistant
          endpoint="video"
          testIdPrefix="video-assist"
          hasContext={!!startFrame?.storage_path}
          contextHint="Sube el Start Frame para empezar."
          contextPayload={{
            start_frame_path: startFrame?.storage_path,
            end_frame_path: useEndFrame ? (endFrame?.storage_path || null) : null,
            model,
            duration_seconds: duration,
          }}
          onUsePrompt={(text) => setPrompt(text)}
        />
        <PromptVoiceField
          value={prompt}
          onChange={setPrompt}
          testIdPrefix="video-prompt-voice"
          hint="Prompt de movimiento para Kling (cámara, transición, estilo cinematográfico)."
        >
          <textarea
            data-testid="video-prompt"
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            placeholder="Ej: slow cinematic luxury camera push in, realistic motion, soft volumetric light"
            className="w-full px-4 py-3 pr-11 rounded-xl bg-white/50 border border-gray-200/50 text-sm text-gray-900 outline-none focus:border-gray-300 placeholder:text-gray-400 resize-none min-h-[100px]"
          />
        </PromptVoiceField>
      </div>

      {/* Error */}
      {error && (
        <div data-testid="video-error" className="px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Submit */}
      <div className="flex items-center gap-3 flex-wrap">
        <button
          data-testid="video-submit"
          onClick={handleSubmit}
          disabled={!canSubmit}
          className={`flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-medium transition-all active:scale-[0.98] ${
            !canSubmit
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
              : 'bg-gray-900 text-white hover:bg-gray-800'
          }`}
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Film className="w-4 h-4" />}
          Generar Video
        </button>
        {activeJobs.length > 0 && (
          <span
            data-testid="video-active-counter"
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium bg-gray-900/[0.06] text-gray-700 border border-gray-200/70"
          >
            <Loader2 className="w-3 h-3 animate-spin" />
            {activeJobs.length} en proceso
          </span>
        )}
      </div>

      {/* Active / failed queue */}
      {(activeJobs.length > 0 || failedJobs.length > 0) && (
        <div data-testid="video-jobs-panel" className="space-y-1.5">
          <h3 className="text-xs text-gray-400 uppercase tracking-wider">Cola de trabajos</h3>
          {[...activeJobs, ...failedJobs.slice(0, 3)].map(j => {
            const labels = { queued: 'En cola', processing: 'Procesando', failed: 'Error' };
            const dot = j.status === 'failed'
              ? 'bg-red-500'
              : j.status === 'processing' ? 'bg-emerald-500' : 'bg-amber-400';
            return (
              <div
                key={j.id}
                data-testid={`video-job-row-${j.id}`}
                className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white/55 border border-gray-200/50"
              >
                <span className={`w-2 h-2 rounded-full shrink-0 ${dot} ${j.status === 'processing' ? 'animate-pulse' : ''}`} />
                <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-gray-500 shrink-0">
                  {j.status === 'processing' && <Loader2 className="w-3 h-3 animate-spin" />}
                  {labels[j.status]}
                </span>
                <span className="text-[11px] text-gray-400 shrink-0">
                  {j.model.replace('kling-2.6-', '')} · {j.duration_seconds}s · {j.aspect_ratio} · {j.mode === 'start_end_frame' ? 'start+end' : 'i2v'}
                </span>
                <span className="text-sm text-gray-700 truncate flex-1" title={j.prompt}>
                  {j.prompt}
                </span>
                {j.status === 'failed' && j.error_message && (
                  <span className="text-[11px] text-red-500 max-w-[260px] truncate" title={j.error_message}>
                    {j.error_message}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Completed results */}
      {completedJobs.length > 0 && (
        <div data-testid="video-results" className="space-y-3">
          <h3 className="text-xs text-gray-400 uppercase tracking-wider">Videos generados</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {completedJobs.slice(0, 8).map(j => (
              <VideoResultCard key={j.id} job={j} projectId={projectId} projects={projects} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Result card ---------------------------------------------------------
function VideoResultCard({ job, projectId, projects }) {
  const url = job.result_video_url;
  const projectLabel = projects?.find(p => p.id === job.project_id)?.name;

  const handleDownload = () => {
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = `ruido-video-${job.id.slice(0, 8)}.mp4`;
    a.target = '_blank';
    a.rel = 'noopener';
    a.click();
  };

  return (
    <div className="rounded-xl border border-gray-200/50 bg-white/50 overflow-hidden">
      <div className="relative aspect-square bg-black">
        {url ? (
          <video
            data-testid={`video-preview-${job.id}`}
            src={url}
            controls
            playsInline
            className="w-full h-full object-contain"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/60 text-xs">
            Sin preview
          </div>
        )}
      </div>
      <div className="px-3 py-2 flex flex-col gap-1.5">
        <p className="text-xs text-gray-600 line-clamp-2" title={job.prompt}>
          {job.prompt}
        </p>
        <div className="flex items-center gap-1 flex-wrap pt-1">
          <span className="text-[10px] uppercase tracking-wider text-gray-400">
            {job.model.replace('kling-2.6-', '')} · {job.duration_seconds}s · {job.aspect_ratio}
            {job.project_id && projectLabel && ` · ${projectLabel}`}
          </span>
          <div className="ml-auto flex items-center gap-1">
            <button
              data-testid={`video-download-${job.id}`}
              onClick={handleDownload}
              disabled={!url}
              className="p-1.5 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-900/5 disabled:opacity-40"
              title="Descargar"
            >
              <Download className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
